import { Beech } from './beech';
import { RTC } from './lib/irtc';
import { Call } from '@twilio/voice-sdk';

export interface MetricsSample {
	timestamp: number;
	bytesSent: number;
	bytesReceived: number;
	packetsSent: number;
	packetsReceived: number;
	packetsLost: number;
	fractionLost: number;
	jitter: number;
	remoteAudioLevel: number;
	localAudioLevel: number;
	roundTripTime: number;
}

export default class Metrics {
	private beech: Beech;

	private statsInterval: number | null = null;
	private activeCallSid: string | null = null;

	private lastSample: MetricsSample | null = null;

	constructor(beech: Beech) {
		logg.log('metrics >> init');

		this.beech = beech;
	}

	public intercept = (connection: Call) => {
		logg.log('metrics >> intercept', connection);

		// unofficial - Intercept enable/disable to add our own monitoring
		let monitor = (<any>connection)._monitor;
		if (typeof monitor !== 'undefined' && typeof monitor.orig_enable === 'undefined') {
			let metrics = this;
			let RTCMonitor = Object.getPrototypeOf(monitor);

			RTCMonitor.orig_enable = RTCMonitor.enable;
			RTCMonitor.enable = function (this: any, peerConnection: RTC.PeerConnection) {
				this.orig_enable(peerConnection, connection);
				metrics.enable(peerConnection, connection);
			};

			RTCMonitor.orig_disable = RTCMonitor.disable;
			RTCMonitor.disable = function (this: any) {
				this.orig_disable();
				metrics.disable();
			};
		}
	};

	private enable = (peerConnection: RTC.PeerConnection, call: Call) => {
		logg.log('metrics >> enable', peerConnection);

		if (this.statsInterval) {
			window.clearInterval(this.statsInterval);
		}
		this.activeCallSid = call.parameters.CallSid;
		this.statsInterval = window.setInterval(() => {
			this.pollStats(peerConnection);
		}, 1000);
	};

	private disable = () => {
		logg.log('metrics >> disable');

		if (this.statsInterval) {
			window.clearInterval(this.statsInterval);
		}
		this.statsInterval = null;
		this.activeCallSid = null;
	};

	private pollStats = (pc: RTC.PeerConnection) => {
		if (!this.beech.persist.getTrackCallMetrics()) {
			// disabled
			return;
		}

		pc.getStats().then(this.extractSample).then(this.diffSample).then(this.uploadDiff);
	};

	private extractSample = (report: RTC.StatsReport): MetricsSample => {
		let sample: any = {};
		let index = 0;
		report.forEach((stats: RTC.Stats) => {
			if (stats.type === 'inbound-rtp') {
				let inboundStats = <RTC.InboundRTPStreamStats>stats;
				sample.timestamp = inboundStats.timestamp;
				sample.bytesReceived = inboundStats.bytesReceived;
				sample.packetsReceived = inboundStats.packetsReceived;
				sample.packetsLost = inboundStats.packetsLost;
				sample.fractionLost = inboundStats.fractionLost;
				sample.jitter = inboundStats.jitter;
			} else if (stats.type === 'outbound-rtp') {
				let outboundStats = <RTC.OutboundRTPStreamStats>stats;
				sample.bytesSent = outboundStats.bytesSent;
				sample.packetsSent = outboundStats.packetsSent;
			} else if (stats.type === 'track') {
				let trackStats = <RTC.MediaStreamTrackStats>stats;
				if (trackStats.remoteSource) {
					sample.remoteAudioLevel = trackStats.audioLevel;
				} else {
					sample.localAudioLevel = trackStats.audioLevel;
				}
			} else if (stats.type === 'candidate-pair') {
				let pairStats = <RTC.IceCandidatePairStats>stats;
				if (pairStats.state == 'succeeded') {
					sample.roundTripTime = pairStats.currentRoundTripTime * 1000;
				}
			}

			// Debug
			index++;
			for (let field in stats) {
				if (stats.hasOwnProperty(field)) {
					//	sample[index + '-' + stats.type + '-' + field] = (<any>stats)[field];
				}
			}
		});

		return sample;
	};

	private diffSample = (sample: MetricsSample): MetricsSample => {
		let currentSample = JSON.parse(JSON.stringify(sample)); // clone

		if (this.lastSample) {
			sample.bytesSent -= this.lastSample.bytesSent;
			sample.bytesReceived -= this.lastSample.bytesReceived;
			sample.packetsSent -= this.lastSample.packetsSent;
			sample.packetsReceived -= this.lastSample.packetsReceived;
			sample.packetsLost -= this.lastSample.packetsLost;
		}

		this.lastSample = currentSample;
		return sample;
	};

	private uploadDiff = (diff: MetricsSample) => {
		console.log('metrics >> upload', diff);

		if (this.activeCallSid != null) {
			this.beech.server.logMetrics(this.activeCallSid, diff);
		}
	};
}
